import { useLanguage } from "@/context/LanguageContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { Seo } from "@/components/Seo";
import { Eyebrow } from "@/components/shared";
import { TripEnquiryForm } from "@/components/TripEnquiryForm";
import { SITE, telHref, waHref, DEFAULT_WA_TEXT } from "@/config/site";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const NumberBlock = ({ data, label, testPrefix }) => {
  const { t } = useLanguage();
  return (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`contact-${testPrefix}-block`}>
    <span className="text-xs font-bold uppercase tracking-wider text-amber-600">{label}</span>
    <p className="mt-1 text-lg font-extrabold text-slate-900">{data.display}</p>
    <div className="mt-3 grid grid-cols-2 gap-2">
      <a href={telHref(data.tel)} data-testid={`contact-${testPrefix}-call`} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700">
        <Phone className="h-4 w-4" /> {t("Call")}
      </a>
      <a href={waHref(data.wa, t(DEFAULT_WA_TEXT))} target="_blank" rel="noopener noreferrer" data-testid={`contact-${testPrefix}-whatsapp`} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
        <WhatsAppIcon className="h-4 w-4" /> {t("WhatsApp")}
      </a>
    </div>
  </div>
  );
};

export default function Contact() {
  const { t, cityName } = useLanguage();
  return (
    <>
      <Seo
        title={t("Taxi & Tempo Traveller Booking | Contact Our Satna Team")}
        description={t("Contact our Satna team for taxi services, car hire, cab booking or Tempo Traveller travel. Call or WhatsApp +91 96857 64656 with your route and dates.")}
      />
      <section className="bg-[#071426]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Eyebrow className="text-amber-400">{t("Contact")}</Eyebrow>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">{t("Contact us for taxi & Tempo Traveller booking")}</h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300">{t("Contact us for taxi and Tempo Traveller services, car hire or cab booking. Share your pickup, destination, dates and passenger count by phone, WhatsApp or the enquiry form so we can check availability and provide a quote.")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-extrabold text-slate-900">{t("Contact details")}</h2>
            <p className="mt-2 text-sm text-slate-600">{t("Both numbers support calls and WhatsApp. Use the primary number for the quickest response.")}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <NumberBlock data={SITE.phonePrimary} label={t("Primary (preferred)")} testPrefix="primary" />
              <NumberBlock data={SITE.phoneSecondary} label={t("Alternate")} testPrefix="secondary" />
            </div>

            <div className="mt-5 space-y-3">
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-300">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><Mail className="h-5 w-5" /></span>
                <span><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">{t("Email")}</span><span className="block font-semibold text-slate-900">{SITE.email}</span></span>
              </a>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="contact-address-block">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><MapPin className="h-5 w-5" /></span>
                <span><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">{t("Base")}</span><span className="block font-semibold text-slate-900">{cityName(SITE.addressShort)}</span></span>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><Clock className="h-5 w-5" /></span>
                <span><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">{t("Response")}</span><span className="block font-semibold text-slate-900">{t("We reply to enquiries with a quote; prebook outstation trips in advance.")}</span></span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-extrabold text-slate-900">{t("Send a trip enquiry")}</h2>
            <p className="mt-1 text-sm text-slate-600">{t("We'll email our team and call you back. Required fields are marked with an asterisk.")}</p>
            <div className="mt-5"><TripEnquiryForm sourcePage="contact-page" /></div>
          </div>
        </div>
      </section>
    </>
  );
}
